import React, { useCallback, useMemo, useState } from 'react'
import { makeStyles, ActionButton, ConfirmationDialog, Grid, StatusDot, TableRow, Typography } from '@paintscout/ui'
import type { MuiTheme as Theme, StyleClasses } from '@paintscout/ui'
import {
  EstimatorAvatar,
  useClientOptions,
  useDialogs,
  useUser,
  useUserPermissions,
  useUsers
} from '@paintscout/ui-business'
import { useSearchQuotesQuery } from '@paintscout/api'
import type { SearchedQuoteFragment, SearchQuotesQuery } from '@paintscout/api'
import type { QuoteStatus } from 'paintscout'
import { useHistory } from 'react-router-dom'
import { getActivityColor } from '@paintscout/util'
import { formatCurrency, getObjectLabels } from '@paintscout/util/builder'
import LinkOffIcon from '@material-ui/icons/LinkOff'
import ArchiveIcon from '@material-ui/icons/Archive'
import DeleteIcon from '@material-ui/icons/Delete'
import EditIcon from '@material-ui/icons/Edit'
import { useQuoteMutations } from 'shared/hooks'
import { Sentry } from 'src/util/appSentry'
import { CopyQuoteDialog } from 'src/components/dialogs'
import { useSnackbar } from 'notistack'
import QuoteTable from './QuoteTable'
import type { QuoteRow } from './QuoteTable'

const useStyles = makeStyles<Theme, QuoteDocListProps>(
  (theme) => ({
    root: {},
    quoteDocItem: {
      display: 'flex',
      justifyContent: 'flex-start',
      alignItems: 'center',
      padding: theme.spacing(2),
      '&:hover': {
        cursor: 'pointer',
        backgroundColor: theme.paintscout.palette.brand.Neutral.P96
      },
      '&:not(:last-child)': {
        borderBottom: `2px solid ${theme.paintscout.palette.brand.Neutral.P90}`
      }
    },
    subtitle: {
      color: theme.paintscout.palette.brand.Neutral.P70,
      paddingLeft: theme.spacing(2)
    },
    actionsContainer: {
      '@media print': {
        display: 'none'
      }
    }
  }),
  { name: 'QuoteDocList' }
)

export interface QuoteDocListProps {
  classes?: StyleClasses<typeof useStyles>
  query?: string
  limit?: number
  quotes?: SearchedQuoteFragment[]
  isInvoice?: boolean
  title?: string
  noResultsMessage?: string
  isLoading?: boolean
  hideActions?: boolean
  onRemove?: (id: string) => void
  onDelete?: (ids: string[]) => void
  handleCreate?: () => void
}

function QuoteDocList(props: QuoteDocListProps) {
  const classes = useStyles(props)
  const { options } = useClientOptions()
  const {
    query,
    limit = 5,
    quotes: propsQuotes = null,
    onRemove,
    onDelete,
    isLoading,
    isInvoice,
    title,
    handleCreate,
    hideActions = false,
    noResultsMessage
  } = props
  const [currentPage, setCurrentPage] = useState(1)

  const { loading, data, fetchMore, updateQuery, refetch } = useSearchQuotesQuery({
    variables: { query, limit },
    fetchPolicy: 'network-only',
    skip: !!propsQuotes
  })
  const objectLabels = getObjectLabels({ options, invoice: isInvoice })
  const { deleteQuotes, archiveQuotes } = useQuoteMutations()
  const { openDialog, dismissDialog } = useDialogs()
  const { enqueueSnackbar } = useSnackbar()
  const { userPictures, users } = useUsers()
  const { trialExpired } = useUser()
  const history = useHistory()
  const {
    permissionToEditAssignedInvoice,
    permissionToEditAssignedQuote,
    permissionToEditAllInvoice,
    permissionToEditAllQuote
  } = useUserPermissions()

  const { quotes, totalRows } = useMemo(() => {
    if (propsQuotes) {
      return {
        quotes: propsQuotes,
        totalRows: propsQuotes.length
      }
    }
    return {
      quotes: (data?.searchQuotes?.quotes ?? []).map((quote) => ({
        ...quote,
        id: quote._id
      })),
      totalRows: data?.searchQuotes?.total_rows ?? 0
    }
  }, [data, propsQuotes])

  function handleOnRemove(id: string) {
    if (!propsQuotes) {
      updateQuery?.((cache: SearchQuotesQuery) => {
        if (!cache?.searchQuotes) {
          return cache
        }

        return {
          ...cache,
          searchQuotes: {
            ...cache.searchQuotes,
            tasks: cache.searchQuotes.quotes.filter((quote) => quote._id !== id)
          }
        }
      })
    }
    onRemove(id)
  }

  const handleRowClick = (row: QuoteRow) => {
    history.push(`/${row.invoice ? 'invoices' : 'quotes'}/edit/${row._id}`)
  }

  const actionsForRow = (_row: QuoteRow) => ({
    remove: onRemove ? true : false,
    edit: true,
    archive: true,
    delete: true
  })

  const rowActions = useCallback(
    (row: QuoteRow) => {
      const canWriteAllType = row.invoice ? permissionToEditAllInvoice : permissionToEditAllQuote
      const canWriteAssignedType = row.invoice ? permissionToEditAssignedInvoice : permissionToEditAssignedQuote
      const allowedActions = []
      const actions = actionsForRow(row)
      if (actions.edit && (row.isOwner || canWriteAllType || (row.isAssigned && canWriteAssignedType))) {
        allowedActions.push({
          key: 'edit',
          label: 'Edit',
          icon: EditIcon
        })
      }

      if (actions.archive && (row.isOwner || canWriteAllType || (row.isAssigned && canWriteAssignedType))) {
        allowedActions.push({
          key: 'archive',
          label: 'Archive',
          icon: ArchiveIcon
        })
      }

      if (actions.delete && (row.isOwner || canWriteAllType || (row.isAssigned && canWriteAssignedType))) {
        allowedActions.push({
          key: 'delete',
          label: 'Delete',
          icon: DeleteIcon
        })
      }

      if (actions.remove && (row.isOwner || canWriteAllType || (row.isAssigned && canWriteAssignedType))) {
        allowedActions.push({
          key: 'remove',
          label: 'Remove Association',
          icon: LinkOffIcon
        })
      }
      if (allowedActions.length > 0) {
        return allowedActions
      } else {
        return []
      }
    },
    [actionsForRow]
  )

  const handleAction = (action: string, quoteRows: QuoteRow[]) => {
    const ids = quoteRows.map((quote) => quote._id)

    if (action === 'edit') {
      return history.push(`/${quoteRows[0].invoice ? 'invoices' : 'quotes'}/edit/${quoteRows[0]._id}`)
    }

    switch (action) {
      case 'remove':
        console.log('removing')
        if (onRemove && ids[0]) {
          onRemove(ids[0])
        }
        break
      case 'copy':
        if (ids.length === 1) {
          openDialog(CopyQuoteDialog, {
            quoteId: ids[0],
            isInvoice: history?.location?.pathname?.includes('invoices')
          })
        }
        break
      case 'delete':
        openDialog(ConfirmationDialog, {
          message:
            ids.length > 1
              ? `These ${objectLabels.quote.plural.toLowerCase()} will no longer be accessible and you cannot undo this action.`
              : `This ${objectLabels.quote.value.toLowerCase()} will no longer be accessible and you cannot undo this action.`,
          scary: false,
          onConfirm: async () => {
            await deleteQuotes(ids, true)
              .catch((error) => {
                Sentry.captureException(error)
                enqueueSnackbar('Unable to delete', { variant: 'error' })
              })
              .then(() => {
                refetch()
              })

            if (onDelete) {
              onDelete(ids)
            }

            dismissDialog()
          },
          onCancel: dismissDialog
        })

        break

      case 'archive':
      case 'unarchive': {
        const unarchive = action === 'unarchive'

        openDialog(ConfirmationDialog, {
          message: (
            <div>
              <p>
                This will {action} the selected {ids.length > 0 ? objectLabels.quote.plural : objectLabels.quote.value}.{' '}
                {!unarchive && (
                  <>
                    Archived {objectLabels.quote.plural} <strong>are not available offline!</strong>
                  </>
                )}
              </p>
            </div>
          ),
          onConfirm: async () => {
            archiveQuotes(ids, { unarchive }).catch((error) => {
              Sentry.captureException(error)
              enqueueSnackbar(`Unable to ${unarchive ? 'unarchive' : 'archive'}`, { variant: 'error' })
            })

            // if (onArchive) {
            //   onArchive(ids, unarchive)
            // }

            dismissDialog()
          },
          onCancel: dismissDialog
        })

        break
      }
    }
  }

  const handleFetchNextPage = (page: number) => {
    const nextPageFirstIndex = (page - 1) * limit
    const dataExists = !!quotes[nextPageFirstIndex]
    if (dataExists) {
      return
    }
    fetchMore({
      variables: {
        limit,
        from: nextPageFirstIndex
      },
      updateQuery: (prev: SearchQuotesQuery, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev
        return Object.assign({}, prev, {
          searchQuotes: {
            ...prev.searchQuotes,
            quotes: [...prev.searchQuotes.quotes, ...fetchMoreResult.searchQuotes.quotes]
          }
        })
      }
    })
  }

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage)
  }

  const renderRow = useCallback((row: QuoteRow) => {
    const { status, number, quoteType, client, value } = row
    const contact = history?.location?.pathname?.includes('people/companies') ? client : null
    const subtitle = [`#${number.toString()}`, contact, quoteType, formatCurrency({ options, value })]
      .filter(Boolean)
      .join(' · ')
    const estimator = users.find((user) => user.name === row.estimator)
    const estimatorName = estimator ? estimator.name : row.estimator
    const actions = rowActions(row)
    return (
      <TableRow key={row._id} data-testid={`quote-${row._id}`} onClick={() => handleRowClick(row)}>
        <Grid container className={classes.quoteDocItem}>
          <Grid item xs style={{ paddingRight: '8px' }}>
            <StatusDot size={9} status={status as QuoteStatus} error={row.error} />
            <Typography variant={'subtitle2'} className={classes.subtitle}>
              {subtitle}
            </Typography>
          </Grid>
          <Grid item>
            <EstimatorAvatar
              userPictures={userPictures}
              user={{
                id: estimator?.user_id ?? row.estimator,
                firstName: (estimatorName ?? '').split(' ')[0],
                lastName: (estimatorName ?? '').split(' ')[1]
              }}
              width="36px"
              height="36px"
              fontSize="0.9rem"
              avatarColor={getActivityColor(estimator?.user_metadata?.color)}
            />
          </Grid>
          {!hideActions && actions.length > 0 && (
            <Grid item className={classes.actionsContainer}>
              <ActionButton
                data-testid="actions"
                actions={actions}
                disabled={trialExpired}
                onActionClick={(ev, action) => handleAction(action, [row])}
              />
            </Grid>
          )}
        </Grid>
      </TableRow>
    )
  }, [])

  return (
    <div className={classes.root}>
      <QuoteTable
        title={title}
        handleCreate={
          isLoading ? null : isInvoice ? (data?.searchQuotes?.total_rows > 0 ? null : handleCreate) : handleCreate
        }
        isInvoice={isInvoice}
        actionsForRow={actionsForRow}
        onRemove={onRemove ? handleOnRemove : undefined}
        onDelete={onDelete}
        dense
        loading={loading}
        hideName
        showStatusName
        noResultsMessage={noResultsMessage}
        quotes={quotes.map((quote) => ({
          ...quote,
          _id: (quote as any)?.id ?? quote._id
        }))}
        totalRows={totalRows}
        page={currentPage}
        rowsPerPage={limit}
        pages={Math.ceil(totalRows / limit)}
        onPageChange={handlePageChange}
        onFetchNextPage={handleFetchNextPage}
        disablePagination={totalRows <= limit}
        renderRow={renderRow}
      />
    </div>
  )
}

export default QuoteDocList
